import React from "react";
import DataTable from "react-data-table-component";
import Loader from "./Loader";

const CustomPagination = ({ currentPage, rowCount, rowsPerPage, onChangePage }) => {
  if (!rowCount || rowCount === 0) return null;

  const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));
  const start = (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, rowCount);

  const getPageNumbers = () => {
    const pages = [];
    const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
    const maxVisible = isMobile ? 3 : 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">
      <div className="text-sm text-gray-600 order-2 sm:order-1">
        Showing <span className="font-semibold">{start}-{end}</span> of{" "}
        <span className="font-semibold">{rowCount}</span>
      </div>
      <div className="flex gap-1 sm:gap-2 items-center order-1 sm:order-2">
        <button
          type="button"
          onClick={() => onChangePage(1)}
          disabled={currentPage <= 1}
          className="px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 bg-white min-w-[32px] h-[32px] flex items-center justify-center font-medium"
        >
          <span className="hidden sm:inline">First</span>
          <span className="sm:hidden text-lg">«</span>
        </button>
        {getPageNumbers().map((pageNum) => (
          <button
            key={pageNum}
            type="button"
            onClick={() => onChangePage(pageNum)}
            className={`min-w-[32px] h-[32px] flex items-center justify-center text-xs sm:text-sm border rounded font-medium transition-all ${
              currentPage === pageNum
                ? "bg-red-600 text-white border-red-600 shadow-sm scale-110 z-10"
                : "border-gray-300 hover:bg-gray-100 bg-white text-gray-700 hover:border-gray-400"
            }`}
          >
            {pageNum}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChangePage(totalPages)}
          disabled={currentPage >= totalPages}
          className="px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 bg-white min-w-[32px] h-[32px] flex items-center justify-center font-medium"
        >
          <span className="hidden sm:inline">Last</span>
          <span className="sm:hidden text-lg">»</span>
        </button>
      </div>
    </div>
  );
};

const tableStyles = `
  .ci-inquiry-table .rdt_TableHeadRow .rdt_TableCol svg,
  .ci-inquiry-table .rdt_TableHeadRow .rdt_TableCol [class*="sort"] {
    display: none !important;
  }
  .ci-inquiry-table .rdt_Pagination {
    padding: 0 !important;
    margin: 0 !important;
    min-height: 0 !important;
  }
  .ci-inquiry-table .rdt_TableBody .rdt_TableCell > * {
    pointer-events: none;
  }
  .ci-inquiry-table .rdt_TableBody .rdt_TableCell .ci-action-cell,
  .ci-inquiry-table .rdt_TableBody .rdt_TableCell .ci-action-cell *,
  .ci-inquiry-table .rdt_TableBody .rdt_TableCell .ci-status-badge,
  .ci-inquiry-table .rdt_TableBody .rdt_TableCell button,
  .ci-inquiry-table .rdt_TableBody .rdt_TableCell button * {
    pointer-events: auto !important;
  }
  .ci-inquiry-table .rdt_TableCol:first-child,
  .ci-inquiry-table .rdt_TableCell:first-child {
    flex: 0 0 70px !important;
    min-width: 70px !important;
    max-width: 70px !important;
  }
  .ci-inquiry-table .rdt_TableCell:has(.ci-status-badge) {
    flex-shrink: 0 !important;
    overflow: visible !important;
  }
  .ci-inquiry-table .ci-status-badge {
    max-width: none !important;
    white-space: nowrap !important;
  }
  .ci-inquiry-table.ci-rows-clickable .rdt_TableRow {
    cursor: pointer;
  }
`;

export const renderStatusBadge = (statusName, statusColor) => {
  const colorClasses = {
    grey: "bg-gray-100 text-gray-700 border-gray-300",
    gray: "bg-gray-100 text-gray-700 border-gray-300",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    orange: "bg-orange-50 text-orange-700 border-orange-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    red: "bg-red-50 text-red-700 border-red-200",
    green: "bg-green-50 text-green-700 border-green-200",
  };
  const key = (statusColor || "grey").toLowerCase();
  const cls = colorClasses[key] || "bg-gray-100 text-gray-700 border-gray-300";
  return (
    <span className={`ci-status-badge inline-flex items-center justify-center px-2.5 py-1 text-[10px] font-bold leading-tight border rounded-md whitespace-nowrap ${cls}`}>
      {statusName || "—"}
    </span>
  );
};

export const buildSerialColumn = (currentPage, rowsPerPage) => ({
  name: "S.No",
  id: "sno",
  width: "70px",
  sortable: true,
  sortIndex: 0,
  selector: (row) => row.inquiry_id || row.id,
  cell: (row) => (
    <span className="text-gray-500 font-bold text-xs tabular-nums block text-center w-full">
      {row.inquiry_id || row.id}
    </span>
  ),
});

const customStyles = {
  headRow: {
    style: {
      backgroundColor: "#f8fafc",
      color: "#6b7280",
      fontWeight: "800",
      fontSize: "10px",
      borderBottom: "1px solid #e5e7eb",
      minHeight: "44px",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
  },
  headCells: {
    style: {
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  cells: {
    style: {
      paddingLeft: "16px",
      paddingRight: "16px",
      fontSize: "12px",
      paddingTop: "14px",
      paddingBottom: "14px",
      verticalAlign: "top",
    },
  },
  rows: {
    style: {
      minHeight: "56px",
      borderBottom: "1px solid #f3f4f6",
      "&:hover": {
        backgroundColor: "#f8fafc",
      },
    },
  },
};

export default function ClientInquiryTable({
  columns,
  data,
  isLoading,
  totalRows,
  currentPage,
  rowsPerPage,
  onPageChange,
  onSort,
  onRowClicked,
  emptyMessage = "No records found",
  keyField = "id",
}) {
  return (
    <div className={`ci-inquiry-table border border-gray-150 rounded-xl overflow-hidden bg-white${onRowClicked ? " ci-rows-clickable" : ""}`}>
      <style>{tableStyles}</style>
      <DataTable
        keyField={keyField}
        columns={columns}
        data={data || []}
        progressPending={isLoading}
        progressComponent={
          <div className="flex flex-col items-center justify-center py-16 min-h-[280px]">
            <Loader />
          </div>
        }
        persistTableHead
        customStyles={customStyles}
        highlightOnHover
        pointerOnHover={Boolean(onRowClicked)}
        onRowClicked={onRowClicked}
        noDataComponent={
          <div className="flex justify-center items-center h-32 text-xs font-bold text-gray-400 uppercase tracking-widest">
            {emptyMessage}
          </div>
        }
        pagination
        paginationServer
        sortServer
        onSort={onSort}
        paginationTotalRows={totalRows}
        paginationPerPage={rowsPerPage}
        paginationDefaultPage={currentPage}
        onChangePage={onPageChange}
        paginationComponent={CustomPagination}
        defaultSortFieldId="sno"
        defaultSortAsc={false}
      />
    </div>
  );
}
